// hooks/useStockInfo.js
import { useMemo, useSyncExternalStore } from 'react';

const EMPTY_STOCK_INFO_SNAPSHOT = {
  data: null,
  error: null,
  status: 'idle',
};

function createStockInfoStore(ticker, apiBaseUrl) {
  let controller = null;
  let requestStarted = false;
  let snapshot = ticker
    ? { ...EMPTY_STOCK_INFO_SNAPSHOT, status: 'loading' }
    : EMPTY_STOCK_INFO_SNAPSHOT;
  const listeners = new Set();

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  const setSnapshot = (nextSnapshot) => {
    snapshot = nextSnapshot;
    notify();
  };

  const loadStockInfo = async () => {
    if (!ticker) {
      setSnapshot(EMPTY_STOCK_INFO_SNAPSHOT);
      return;
    }

    requestStarted = true;
    controller = new AbortController();

    try {
      const encodedTicker = encodeURIComponent(ticker);
      const res = await fetch(`${apiBaseUrl}/stock-info?ticker=${encodedTicker}`, {
        signal: controller.signal,
      });
      if (!res.ok) {
        const error = new Error(`Failed to fetch stock info. Server responded with ${res.status}.`);
        error.status = res.status;
        throw error;
      }
      const stockData = await res.json();
      setSnapshot({
        data: stockData,
        error: null,
        status: 'success',
      });
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (err.status !== 404) {
        console.error('API call failed for useStockInfo:', err);
      }
      setSnapshot({
        data: null,
        error: err.status === 404
          ? 'Ticker not found.'
          : 'Could not load stock metadata. We might not have the data for this ticker yet.',
        errorStatus: err.status || null,
        status: 'error',
      });
    }
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      if (listeners.size === 1 && !requestStarted) loadStockInfo();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          if (controller) {
            controller.abort();
            controller = null;
          }
          requestStarted = false;
        }
      };
    },
  };
}

export function useStockInfo(ticker) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
  const store = useMemo(
    () => createStockInfoStore(ticker, apiBaseUrl),
    [ticker, apiBaseUrl]
  );

  const snapshot = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );

  return useMemo(
    () => ({
      data: snapshot.data,
      error: snapshot.error,
      errorStatus: snapshot.errorStatus || null,
      isNotFound: snapshot.status === 'error' && snapshot.errorStatus === 404,
      loading: snapshot.status === 'loading',
      status: snapshot.status,
    }),
    [snapshot]
  );
}
